import { Check, ChevronDown, Loader2, Search, X, AlertTriangle } from 'lucide-react'
import { useState, useMemo, useRef, useEffect } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  useAssignableMembers,
  type AssignableMember,
  type TrackType,
  type WorkloadLevel,
} from '@/hooks'
import { cn } from '@/lib/utils'

// ==================== Types ====================

interface TeamMemberSelectorProps {
  teamId: string
  mode: 'single' | 'multiple'
  value: string | string[]
  onChange: (value: string | string[]) => void
  placeholder?: string
  disabled?: boolean
  excludeIds?: string[]
  trackType?: TrackType
  teamRole?: string  // 按团队角色筛选（如 product, design, frontend, backend, testing）
  showWorkload?: boolean
  className?: string
}

// ==================== Constants ====================

const ROLE_LABELS: Record<string, string> = {
  product: '产品',
  design: '设计',
  frontend: '前端',
  backend: '后端',
  testing: '测试',
  leader: '负责人',
}

const WORKLOAD_CONFIG: Record<WorkloadLevel, { label: string; color: string; bgColor: string }> = {
  low: { label: '空闲', color: 'text-green-700', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  normal: { label: '正常', color: 'text-blue-700', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  high: { label: '繁忙', color: 'text-orange-700', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  overloaded: { label: '超载', color: 'text-red-700', bgColor: 'bg-red-100 dark:bg-red-900/30' },
}

// ==================== Component ====================

/**
 * 团队成员选择器组件
 * Requirements: 1.1, 1.2, 1.4, 1.5, 1.6
 *
 * - 下拉列表显示团队成员
 * - 显示头像、姓名、角色
 * - 显示当前工作量
 * - 支持搜索筛选
 * - 支持按轨道类型筛选
 * - 支持按团队角色筛选
 */
export function TeamMemberSelector({
  teamId,
  mode,
  value,
  onChange,
  placeholder = '选择人员',
  disabled = false,
  excludeIds = [],
  trackType,
  teamRole,
  showWorkload = true,
  className,
}: TeamMemberSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // 获取可分配成员
  const { data: members, isLoading } = useAssignableMembers({
    teamId,
    trackType,
    teamRole,
    search: search || undefined,
    excludeIds: mode === 'multiple' ? excludeIds : undefined,
  })

  // 筛选后的成员列表
  const filteredMembers = useMemo(() => {
    if (!members) {return []}

    return members.filter((member) => {
      // 单选模式下排除已选中的
      if (mode === 'single' && excludeIds.includes(member.id)) {
        return false
      }
      return true
    })
  }, [members, mode, excludeIds])

  // 当前选中的值
  const selectedValues = useMemo(() => {
    if (mode === 'single') {
      return value ? [value as string] : []
    }
    return (value as string[]) || []
  }, [mode, value])

  // 获取选中成员的信息
  const selectedMembers = useMemo(() => {
    if (!members) {return []}
    return selectedValues
      .map((id) => members.find((m) => m.id === id))
      .filter((m): m is AssignableMember => m !== undefined)
  }, [members, selectedValues])

  // 处理选择
  const handleSelect = (memberId: string) => {
    if (mode === 'single') {
      onChange(memberId)
      setIsOpen(false)
      setSearch('')
    } else {
      const currentValues = (value as string[]) || []
      if (currentValues.includes(memberId)) {
        onChange(currentValues.filter((id) => id !== memberId))
      } else {
        onChange([...currentValues, memberId])
      }
    }
  }

  // 移除选中项
  const handleRemove = (memberId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (mode === 'single') {
      onChange('')
    } else {
      const currentValues = (value as string[]) || []
      onChange(currentValues.filter((id) => id !== memberId))
    }
  }

  // 清空选择
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (mode === 'single') {
      onChange('')
    } else {
      onChange([])
    }
  }

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // 打开时聚焦搜索框
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* 触发器 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex min-h-[38px] w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
          isOpen && 'ring-2 ring-ring ring-offset-2'
        )}
      >
        <div className="flex flex-1 flex-wrap gap-1">
          {selectedValues.length > 0 ? (
            mode === 'single' ? (
              <SingleSelectedDisplay
                member={selectedMembers[0]}
                showWorkload={showWorkload}
              />
            ) : (
              selectedMembers.map((member) => (
                <Badge key={member.id} variant="secondary" className="gap-1 pr-1">
                  <MemberAvatar member={member} size="xs" />
                  <span className="max-w-[100px] truncate">{member.name}</span>
                  <X
                    className="h-3 w-3 cursor-pointer hover:text-destructive"
                    onClick={(e) => handleRemove(member.id, e)}
                  />
                </Badge>
              ))
            )
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedValues.length > 0 && (
            <X
              className="h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
              onClick={handleClear}
            />
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* 下拉面板 */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          {/* 搜索框 */}
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索人员..."
                className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* 成员列表 */}
          <div className="max-h-60 overflow-y-auto p-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                加载中...
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                没有找到匹配的人员
              </div>
            ) : (
              filteredMembers.map((member) => {
                const isSelected = selectedValues.includes(member.id)
                return (
                  <MemberOption
                    key={member.id}
                    member={member}
                    isSelected={isSelected}
                    showWorkload={showWorkload}
                    onClick={() => handleSelect(member.id)}
                  />
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}


// ==================== Sub Components ====================

/**
 * 成员头像
 */
function MemberAvatar({
  member,
  size = 'sm',
}: {
  member: AssignableMember
  size?: 'xs' | 'sm' | 'md'
}) {
  const sizeClasses = {
    xs: 'h-4 w-4',
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
  }

  const textSizes = {
    xs: 'text-[8px]',
    sm: 'text-xs',
    md: 'text-sm',
  }

  return (
    <Avatar className={sizeClasses[size]}>
      {member.avatar && <AvatarImage src={member.avatar} alt={member.name} />}
      <AvatarFallback className={textSizes[size]}>
        {member.name.slice(0, 2)}
      </AvatarFallback>
    </Avatar>
  )
}

/**
 * 工作量指示器
 */
function WorkloadIndicator({ level, taskCount }: { level: WorkloadLevel; taskCount: number }) {
  const config = WORKLOAD_CONFIG[level]

  return (
    <div className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-xs', config.bgColor)}>
      {level === 'overloaded' && <AlertTriangle className="h-3 w-3" />}
      <span className={config.color}>{taskCount} 任务</span>
    </div>
  )
}

/**
 * 单选模式下的选中显示
 */
function SingleSelectedDisplay({
  member,
  showWorkload,
}: {
  member?: AssignableMember
  showWorkload: boolean
}) {
  if (!member) {
    return <span className="text-muted-foreground">未选择</span>
  }

  return (
    <div className="flex items-center gap-2">
      <MemberAvatar member={member} size="sm" />
      <span className="font-medium">{member.name}</span>
      {member.teamRole && (
        <Badge variant="outline" className="text-xs">
          {ROLE_LABELS[member.teamRole] || member.teamRole}
        </Badge>
      )}
      {showWorkload && (
        <WorkloadIndicator level={member.workloadLevel} taskCount={member.activeTaskCount} />
      )}
    </div>
  )
}

/**
 * 成员选项
 */
function MemberOption({
  member,
  isSelected,
  showWorkload,
  onClick,
}: {
  member: AssignableMember
  isSelected: boolean
  showWorkload: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded-md px-2 py-2 text-sm',
        'hover:bg-accent',
        isSelected && 'bg-accent'
      )}
    >
      <div className="flex items-center gap-2">
        <MemberAvatar member={member} size="sm" />
        <div className="flex flex-col items-start">
          <span className="font-medium">{member.name}</span>
          {member.teamRole && (
            <span className="text-xs text-muted-foreground">
              {ROLE_LABELS[member.teamRole] || member.teamRole}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {showWorkload && (
          <WorkloadIndicator level={member.workloadLevel} taskCount={member.activeTaskCount} />
        )}
        {isSelected && <Check className="h-4 w-4 text-primary" />}
      </div>
    </button>
  )
}

// ==================== Exports ====================

export { ROLE_LABELS, WORKLOAD_CONFIG }
export type { TeamMemberSelectorProps, AssignableMember, TrackType, WorkloadLevel }
